/**
 * Компонент узла коммита (круглый значок)
 * Отображает отдельный коммит на графе с хешем, метками веток и анимациями
 */

import { memo, useState } from 'react';
import { cn } from '@/lib/utils';
import type { CommitNodeVisual, GraphConfig, HighlightState } from './types';
import { DEFAULT_GRAPH_CONFIG } from './types';

interface CommitNodeProps {
  node: CommitNodeVisual;
  config?: Partial<GraphConfig>;
  isNew?: boolean;
  isAnimating?: boolean;
  highlight?: HighlightState | undefined;
  onClick?: (sha: string) => void;
}

/**
 * Компонент круглого узла коммита
 */
export const CommitNode = memo(function CommitNode({
  node,
  config,
  isNew = false,
  isAnimating = false,
  highlight,
  onClick,
}: CommitNodeProps) {
  const [isHovered, setIsHovered] = useState(false);
  const fullConfig: GraphConfig = { ...DEFAULT_GRAPH_CONFIG, ...config };
  const { commit, position, branchInfo, isHead, isMerge, branchLabels } = node;

  const radius = isMerge ? fullConfig.mergeNodeRadius : fullConfig.nodeRadius;
  const color = branchInfo?.hexColor || '#6b7280';

  // Сокращённый хеш (первые 6 символов)
  const shortSha = commit.sha.slice(0, 6);

  // Форматирование даты
  const date = new Date(commit.timestamp);
  const dateStr = date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: 'short',
  });

  return (
    <g
      transform={`translate(${position.x}, ${position.y})`}
      className="commit-node"
      onClick={() => onClick?.(commit.sha)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      {/* Внешнее кольцо для HEAD */}
      {isHead && (
        <circle
          r={radius + 4}
          fill="none"
          stroke={color}
          strokeWidth={2}
          strokeDasharray="4 2"
          className={cn('animate-head-ring', isAnimating && 'animate-head-ring-pulse')}
        />
      )}

      {/* Кольцо подсветки из сценария */}
      {highlight && (
        <circle
          r={radius + 6}
          fill="none"
          stroke={highlight.color}
          strokeWidth={3}
          className={cn(
            'transition-all duration-300',
            highlight.animation === 'pulse' && 'animate-highlight-pulse',
            highlight.animation === 'glow' && 'animate-highlight-glow',
            highlight.animation === 'bounce' && 'animate-highlight-bounce'
          )}
          style={{
            filter:
              highlight.animation === 'glow' ? `drop-shadow(0 0 6px ${highlight.color})` : 'none',
          }}
        />
      )}

      {/* Основной круг коммита */}
      <circle
        r={radius}
        fill={color}
        stroke={isHead ? '#ffffff' : 'rgba(255,255,255,0.2)'}
        strokeWidth={isHead ? 2 : 1}
        className={cn(
          'transition-all duration-300',
          isNew && 'animate-node-appear',
          isAnimating && 'animate-node-pulse',
          isHovered && 'filter-brightness-110'
        )}
        style={{
          filter: isHovered ? 'brightness(1.2)' : 'none',
        }}
      />

      {/* Хеш коммита внутри круга */}
      <text
        textAnchor="middle"
        dominantBaseline="middle"
        fill="#ffffff"
        fontSize={8}
        fontFamily="monospace"
        fontWeight="bold"
        className="select-none pointer-events-none"
      >
        {shortSha}
      </text>

      {/* Метки веток справа от узла */}
      {branchLabels.length > 0 && (
        <g transform={`translate(${radius + 8}, 0)`}>
          {branchLabels.map((label, index) => {
            const labelColor =
              label === 'main' || label === 'master'
                ? '#F05032'
                : label.startsWith('feature/')
                  ? '#4a9eff'
                  : label === 'develop'
                    ? '#8b5cf6'
                    : label.startsWith('hotfix/')
                      ? '#fbbf24'
                      : label.startsWith('release/')
                        ? '#34d399'
                        : '#6b7280';

            return (
              <g
                key={label}
                transform={`translate(0, ${index * 16 - (branchLabels.length - 1) * 8})`}
              >
                <rect
                  x={0}
                  y={-8}
                  width={label.length * 7 + 8}
                  height={16}
                  rx={4}
                  fill={labelColor}
                  opacity={0.9}
                  className={cn('transition-opacity duration-200', isNew && 'animate-label-appear')}
                />
                <text
                  x={4}
                  y={0}
                  dominantBaseline="middle"
                  fill="#ffffff"
                  fontSize={10}
                  fontFamily="monospace"
                  fontWeight="medium"
                  className="select-none pointer-events-none"
                >
                  {label}
                </text>
              </g>
            );
          })}
        </g>
      )}

      {/* Tooltip при наведении */}
      {isHovered && (
        <g transform={`translate(${radius + 100}, 0)`}>
          <foreignObject x={-100} y={-40} width={200} height={80} className="overflow-visible">
            <div className="rounded-lg bg-slate-800 p-2 shadow-lg border border-slate-700 text-xs">
              <div className="flex items-center gap-2 mb-1">
                <code className="font-mono text-slate-400">{commit.sha}</code>
                {isHead && (
                  <span className="px-1 py-0.5 rounded bg-git-orange/20 text-git-orange text-[10px]">
                    HEAD
                  </span>
                )}
              </div>
              <p className="text-slate-200 text-sm truncate">{commit.message}</p>
              <p className="text-slate-500 text-[10px] mt-1">
                {commit.author} • {dateStr}
              </p>
            </div>
          </foreignObject>
        </g>
      )}
    </g>
  );
});
